import { FC, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { KTSVG } from 'src/_metronic/helpers';
import { AvailableGroup } from './useUserShortcuts';

interface Props {
  open: boolean;
  onClose: () => void;
  availableByGroup: AvailableGroup[];
  onToggle: (key: string) => void;
}

const ShortcutPicker: FC<Props> = ({ open, onClose, availableByGroup, onToggle }) => {
  const [search, setSearch] = useState('');

  // Esc ile kapat
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Açılışta aramayı sıfırla
  useEffect(() => {
    if (open) setSearch('');
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr');
    if (!q) return availableByGroup;
    return availableByGroup
      .map((g) => ({ ...g, items: g.items.filter((it) => it.label.toLocaleLowerCase('tr').includes(q)) }))
      .filter((g) => g.items.length > 0);
  }, [search, availableByGroup]);

  const pinnedCount = useMemo(() => availableByGroup.reduce((acc, g) => acc + g.items.filter((i) => i.pinned).length, 0), [availableByGroup]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div className="shortcut-picker__backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
          <motion.div
            className="shortcut-picker"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 400, damping: 40 }}
            role="dialog"
            aria-modal="true"
            aria-label="Kısayol yönetimi"
          >
            <div className="shortcut-picker__handle" />

            <div className="shortcut-picker__header">
              <div className="shortcut-picker__heading">
                <h3 className="shortcut-picker__title">
                  <KTSVG path="/media/icons/duotone/Layout/Layout-grid.svg" className="shortcut-picker__title-icon" />
                  Kısayollarım
                </h3>
                <span className="shortcut-picker__subtitle">{pinnedCount} kısayol seçili · yalnızca erişebildiğiniz sayfalar listelenir</span>
              </div>
              <button type="button" className="shortcut-picker__close" onClick={onClose} title="Kapat">
                <KTSVG path="/media/icons/duotone/Navigation/Close.svg" className="shortcut-picker__close-icon" />
              </button>
            </div>

            <div className="shortcut-picker__search">
              <KTSVG path="/media/icons/duotone/General/Search.svg" className="shortcut-picker__search-icon" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Sayfa ara..." autoFocus />
            </div>

            <div className="shortcut-picker__body">
              {filtered.length === 0 && (
                <div className="shortcut-picker__no-result">
                  <KTSVG path="/media/icons/duotone/General/Sad.svg" className="me-2" />
                  Eşleşen sayfa bulunamadı.
                </div>
              )}

              {filtered.map((g) => (
                <div className="shortcut-picker__group" key={g.group}>
                  <div className="shortcut-picker__group-title">{g.group}</div>
                  <div className="shortcut-picker__grid">
                    {g.items.map((it) => (
                      <motion.button
                        key={it.key}
                        type="button"
                        className={`shortcut-picker__item ${it.pinned ? 'is-pinned' : ''}`}
                        onClick={() => onToggle(it.key)}
                        whileHover={{ y: -2 }}
                        whileTap={{ scale: 0.97 }}
                        title={it.pinned ? 'Kısayoldan kaldır' : 'Kısayol olarak ekle'}
                      >
                        <span className="shortcut-picker__item-icon">
                          <KTSVG path={it.icon} className="shortcut-picker__item-svg" />
                        </span>
                        <span className="shortcut-picker__item-label">{it.label}</span>
                        <KTSVG
                          path={it.pinned ? '/media/icons/duotone/Navigation/Check.svg' : '/media/icons/duotone/Navigation/Plus.svg'}
                          className="shortcut-picker__item-check"
                        />
                      </motion.button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default ShortcutPicker;
